import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import {
  InformationBox,
  ProductLine,
  TitleWithIcon,
  ProductLinesContainer,
} from 'components';
import { Session, useSessionOrders, useSessionProductLines } from '@app/api';

import {
  computeToBeBilledAmount,
  computeAlreadyBilledAmount,
} from 'lib/orders';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2 + 'px',
  },
  title: {
    marginBottom: 0,
    marginTop: 0,
  },
  infoBox: {
    margin: 0,
  },
  total: {
    textTransform: 'uppercase',
    '& > td': {
      paddingTop: theme.marginBase,
    },
  },
}));

interface Props {
  session: Session;
}

export const UsageDetails = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { data: orders } = useSessionOrders(session.id);
  const { data: productLines } = useSessionProductLines(
    session.organizationId,
    session.id,
  );
  const toBeBilled = computeToBeBilledAmount(orders);
  const alreadyBilled = computeAlreadyBilledAmount(orders);

  return (
    <div className={classes.container}>
      <TitleWithIcon
        icon="ri-time-line"
        iconColor="blue"
        title={t('billing.billingDetails.usageDetails')}
        className={classes.title}
      />

      <InformationBox
        text={t('billing.billingDetails.usageDifference')}
        className={classes.infoBox}
        variant="question"
        noIcon
      />

      <ProductLinesContainer>
        {productLines &&
          productLines.map(
            (line) =>
              line.product && (
                <ProductLine
                  key={line.id}
                  label={line.product.title || ''}
                  unitPrice={line.unitPrice}
                  quantity={line.quantity}
                />
              ),
          )}

        <ProductLine
          label={t('billing.billingDetails.totalUsage')}
          price={toBeBilled + alreadyBilled}
          className={classes.total}
          strong
        />
        <ProductLine
          label={t('billing.billingDetails.alreadyBilled')}
          price={alreadyBilled}
        />
        <ProductLine
          label={t('billing.billingDetails.toBeBilled')}
          price={toBeBilled}
        />
      </ProductLinesContainer>
    </div>
  );
};
